import { KnownId, StatusData } from "@fairfleet/geotab";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_SCAN_WINDOW_HOURS } from "../constants";
import { LatestReadings, mergeLatestReadings } from "./latestReadings";
import { changedDiagnosticIds, devicesHoldingAny } from "./lookupChanges";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useLiveDataLookups } from "@/pages/live-data/hooks/useLiveDataLookups";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { LiveDataLookups, toLiveDataRow } from "@/pages/live-data/models/toLiveDataRow";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/** Where a scan has got to, and what it covered. */
export interface FleetScan {
  isRunning: boolean;
  pagesLoaded: number;
  recordsProcessed: number;
  /** How many of the scanned vehicles have reported anything at all so far. */
  deviceCount: number;
  /** Diagnostics read to the end of the window, out of `seriesTotal`. */
  seriesDone: number;
  seriesTotal: number;
  /**
   * Which vehicles the scan finished; anything else has not been checked rather than failed.
   *
   * Every listed vehicle at once, when the last diagnostic is read: before that, a vehicle missing
   * a value may only be waiting for that diagnostic's series.
   */
  scannedIds: Set<string>;
  /** The window it covered, frozen when it started, or null before one has run. */
  window: { from: Date; to: Date } | null;
  error: Error | null;
  /**
   * What has been read so far.
   *
   * The same map throughout a scan, filled in place — copying a fleet's readings per page is
   * exactly what this hook exists to avoid. It travels in the snapshot rather than in a ref so
   * that everything derived from it has an honest dependency: a new snapshot means new readings.
   */
  readings: LatestReadings;
  /**
   * Every diagnostic seen so far, sorted.
   *
   * Sorted because this array is part of the diagnostics query key: in discovery order it changes
   * with every page that brings a new diagnostic, refetching all of them again. A new array only
   * when a diagnostic was added, so the key stays put between most refreshes.
   */
  diagnosticIds: string[];
}

/**
 * A fresh idle scan.
 *
 * A function rather than a shared constant: setting state to the same object makes React skip the
 * re-render, and this hook's readings live in a ref whose changes only become visible through one.
 */
function idleScan(): FleetScan {
  return {
    isRunning: false,
    pagesLoaded: 0,
    recordsProcessed: 0,
    deviceCount: 0,
    seriesDone: 0,
    seriesTotal: 0,
    scannedIds: new Set(),
    window: null,
    error: null,
    readings: new Map(),
    diagnosticIds: [],
  };
}

/**
 * Diagnostics read at the same time, and readings per request.
 *
 * The client holds queued requests for 1.5 s unless 100 are waiting, so every round of requests
 * waits out that timer: with only a few series in flight, a catalog of a few hundred diagnostics
 * spends minutes waiting even when most of them return nothing. With more in flight the pages are
 * kept small instead, because the round travels as one `ExecuteMultiCall` and what crosses the
 * wire at once is the one times the other.
 */
const SERIES_CONCURRENCY = 20;
const RECORDS_PER_PAGE = 5_000;

/** How often the grid is refreshed while the scan runs. */
const REFRESH_INTERVAL_MS = 500;

const MS_PER_HOUR = 60 * 60 * 1000;

/** Before the diagnostics query has answered; rows then carry raw ids until it does. */
const NO_LOOKUPS: LiveDataLookups = { diagnostics: {}, units: {}, sources: {} };

/**
 * Scans what every vehicle reported within a time window.
 *
 * One series per diagnostic the check asks about, for the whole fleet (or the selected groups)
 * at once, rather than one per vehicle: the cost is then a few calls per diagnostic whatever the
 * fleet size, where a series per vehicle cost thousands of calls and kept other pages of the same
 * login waiting on Geotab's rate limit.
 *
 * The scan is started explicitly rather than on mount: it walks the whole fleet's readings and is
 * far too expensive to run because someone opened the page.
 *
 * Only the vehicles a page actually touched are rebuilt on each refresh, and the rest keep their
 * previous arrays. A full rebuild is a pass over every vehicle and every diagnostic — at fleet
 * size that alone was enough to stop the tab responding. The consumer relies on that too: it
 * rebuilds a vehicle's check line only when its array is a new one.
 *
 * @param deviceIds - The vehicles to scan; readings of any other vehicle are dropped. Nothing
 *   happens while this is empty.
 * @param diagnosticIds - The diagnostics to read. Nothing happens while this is empty.
 * @param hoursBack - How far back a scan looks.
 * @param groupIds - Narrows the series to these groups' vehicles; fleet-wide when empty.
 * @param withGpsOdometer - Also reads Geotab's GPS odometer, at one call per vehicle.
 */
export function useFleetStatusFeed(
  deviceIds: string[] = [],
  diagnosticIds: string[] = [],
  hoursBack: number = DEFAULT_SCAN_WINDOW_HOURS,
  groupIds?: string[],
  withGpsOdometer = false,
) {
  const { geotabClient, database, username } = useGeotabClient();

  const abortRef = useRef<AbortController | null>(null);
  const rowsRef = useRef<Record<string, LiveDataRow[]>>({});
  const lookupsRef = useRef<LiveDataLookups>(NO_LOOKUPS);
  const rebuildErrorRef = useRef<Error | null>(null);

  const [scan, setScan] = useState<FleetScan>(idleScan);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    rowsRef.current = {};
    rebuildErrorRef.current = null;
    setScan(idleScan());
  }, []);

  const start = useCallback(() => {
    if (deviceIds.length === 0 || diagnosticIds.length === 0) {
      return;
    }

    clear();
    const controller = new AbortController();
    abortRef.current = controller;

    // Captured once, so the window does not slide while the scan runs and vehicles scanned last
    // are not judged against a different period than those scanned first. Reported back, because
    // the export has to name the window that was actually covered.
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - hoursBack * MS_PER_HOUR);
    const wanted = new Set(deviceIds);

    let pagesLoaded = 0;
    let seriesDone = 0;
    let recordsProcessed = 0;
    let lastRefresh = 0;
    let pendingPublish: ReturnType<typeof setTimeout> | undefined;
    const dirty = new Set<string>();
    const readings: LatestReadings = new Map();
    // The GPS odometer is asked for below, so its name and unit are looked up from the start.
    const seenDiagnostics = new Set<string>(
      withGpsOdometer ? [KnownId.DiagnosticOdometerAdjustmentId] : [],
    );
    let publishedDiagnostics: string[] = [];

    const cancelPendingPublish = () => {
      clearTimeout(pendingPublish);
      pendingPublish = undefined;
    };
    controller.signal.addEventListener("abort", cancelPendingPublish, { once: true });

    // Ends the scan with the error shown. Aborting stops the fetch and makes every later callback
    // a no-op, so the error is not overwritten. No vehicle counts as scanned then: each may still
    // have been missing a diagnostic that was never read.
    const fail = (error: unknown) => {
      if (controller.signal.aborted) {
        return;
      }
      controller.abort();
      setScan(current => ({ ...current, isRunning: false, error: toError(error) }));
    };

    /**
     * @param cause - The error the scan is already ending with. A row error raised while publishing
     *   the last state then does not replace it: the fetch error is the one the user needs to see.
     */
    const publish = (cause?: unknown) => {
      // A late callback of a scan that was stopped or replaced must not write into the new one.
      if (controller.signal.aborted) {
        return;
      }
      cancelPendingPublish();
      lastRefresh = Date.now();
      try {
        rowsRef.current = rebuildRows(rowsRef.current, readings, dirty, lookupsRef.current);
      } catch (error) {
        // Thrown from a timer or a fetch callback, it would be lost and the scan would hang.
        fail(cause ?? error);
        return;
      }
      dirty.clear();
      if (seenDiagnostics.size !== publishedDiagnostics.length) {
        publishedDiagnostics = [...seenDiagnostics].sort();
      }
      const diagnosticIds = publishedDiagnostics;
      setScan(current => ({
        ...current,
        pagesLoaded,
        recordsProcessed,
        deviceCount: readings.size,
        seriesDone,
        diagnosticIds,
      }));
    };

    setScan({
      ...idleScan(),
      isRunning: true,
      window: { from: fromDate, to: toDate },
      seriesTotal: diagnosticIds.length,
      readings,
    });

    // Not due yet: publish once the interval is over instead of waiting for the next event. When
    // the request queue stalls (rate limit, a slow page) there may not be one for a long time, and
    // the grid would show an old state while the scan is still running.
    const publishIfDue = () => {
      const sinceLast = Date.now() - lastRefresh;
      if (sinceLast >= REFRESH_INTERVAL_MS) {
        publish();
      } else if (pendingPublish === undefined) {
        pendingPublish = setTimeout(publish, REFRESH_INTERVAL_MS - sinceLast);
      }
    };

    const scanFleet = () =>
      fetchStatusDataComplete(geotabClient, {
        tasks: diagnosticIds.map(diagnosticId => ({
          key: diagnosticId,
          diagnosticId,
          ...(groupIds?.length ? { groupIds } : {}),
        })),
        fromDate,
        toDate,
        resultsLimit: RECORDS_PER_PAGE,
        maxConcurrency: SERIES_CONCURRENCY,
        signal: controller.signal,
        onPage: (_diagnosticId, page) => {
          if (controller.signal.aborted) {
            return;
          }
          for (const [deviceId, deviceReadings] of byWantedDevice(page, wanted)) {
            recordsProcessed += mergeLatestReadings(readings, deviceId, deviceReadings);
            dirty.add(deviceId);
          }
          pagesLoaded += 1;
          for (const reading of page) {
            if (reading.diagnostic?.id) {
              seenDiagnostics.add(reading.diagnostic.id);
            }
          }
          publishIfDue();
        },
        onSeriesComplete: () => {
          if (controller.signal.aborted) {
            return;
          }
          seriesDone += 1;
          publishIfDue();
        },
      });

    // Geotab's GPS odometer at the end of the window. Only per vehicle: without a device search
    // Geotab interpolates nothing and the answer is empty (checked against aseag on 2026-10-27).
    // That is one call per vehicle, which is why the user switches it on.
    const fetchGpsOdometers = async () => {
      if (!withGpsOdometer || controller.signal.aborted) {
        return;
      }
      await Promise.all(
        deviceIds.map(deviceId =>
          geotabClient
            .get(
              "StatusData",
              {
                deviceSearch: { id: deviceId },
                diagnosticSearch: { id: KnownId.DiagnosticOdometerAdjustmentId },
                fromDate: toDate,
                toDate,
              },
              1,
              controller.signal,
            )
            .then((page: StatusData[]) => {
              if (controller.signal.aborted) {
                return;
              }
              // Interpolated, so without an id: taken in directly rather than through
              // `byWantedDevice`, which drops such rows from the series.
              if (mergeLatestReadings(readings, deviceId, page) > 0) {
                dirty.add(deviceId);
              }
              publishIfDue();
            }),
        ),
      );
    };

    scanFleet()
      .then(fetchGpsOdometers)
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        publish();
        setScan(current => ({ ...current, isRunning: false, scannedIds: new Set(deviceIds) }));
      })
      .catch((error: unknown) => {
        publish(error);
        fail(error);
      });
  }, [clear, deviceIds, diagnosticIds, geotabClient, groupIds, hoursBack, withGpsOdometer]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setScan(current => ({ ...current, isRunning: false }));
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Thrown away when the login changes, because device ids do not carry across databases. Keyed on the
  // credentials rather than on activeSessionId, which stays null for the whole life of the add-in
  // when it runs embedded in MyGeotab.
  const sessionKey = `${username}@${database}`;
  useEffect(() => {
    clear();
  }, [clear, sessionKey]);

  const {
    lookups,
    isLoading: lookupsLoading,
    isError: lookupsError,
  } = useLiveDataLookups(scan.diagnosticIds);

  // The names and units arrive from their own query, later than the readings, and keep arriving
  // while new diagnostics are discovered. Only the vehicles that hold a diagnostic whose lookup
  // changed are built again; until a name lands their rows carry raw ids.
  const rebuilt = useMemo(() => {
    if (lookupsRef.current !== lookups) {
      const changed = changedDiagnosticIds(lookupsRef.current, lookups);
      lookupsRef.current = lookups;
      rebuildErrorRef.current = null;
      try {
        rowsRef.current = rebuildRows(
          rowsRef.current,
          scan.readings,
          devicesHoldingAny(scan.readings, changed),
          lookups,
        );
      } catch (error) {
        // Kept in a ref so a repeated call of this memo reports the same error. The rows stay as
        // they were; the scan ends with this error and export is blocked, so they are not used.
        rebuildErrorRef.current = toError(error);
      }
    }
    return { rowsByDeviceId: rowsRef.current, error: rebuildErrorRef.current };
  }, [scan, lookups]);
  const { rowsByDeviceId } = rebuilt;

  // A failure while applying new lookups ends the scan like one in the fetch does, instead of
  // taking the whole page down from inside render.
  useEffect(() => {
    const error = rebuilt.error;
    if (!error) {
      return;
    }
    abortRef.current?.abort();
    setScan(current => ({ ...current, isRunning: false, error: current.error ?? error }));
  }, [rebuilt.error]);

  // Reported, because rows built without the names fall back to raw ids and then miss every check
  // that matches by name: the result would look like a fault in the vehicles.
  return { rowsByDeviceId, scan, start, stop, lookupsLoading, lookupsError };
}

/**
 * One page of a fleet-wide series, split by vehicle.
 *
 * Vehicles outside the list are dropped: a group or fleet-wide series also returns vehicles the
 * search left out. So are readings without an id, which Geotab interpolates rather than stores.
 */
function byWantedDevice(page: StatusData[], wanted: Set<string>): Map<string, StatusData[]> {
  const byDevice = new Map<string, StatusData[]>();
  for (const reading of page) {
    const deviceId = reading.device?.id;
    if (!reading.id || !deviceId || !wanted.has(deviceId)) {
      continue;
    }
    const list = byDevice.get(deviceId);
    if (list) {
      list.push(reading);
    } else {
      byDevice.set(deviceId, [reading]);
    }
  }
  return byDevice;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Rebuilds the rows of the vehicles named in `dirty`, keeping the rest as they were.
 *
 * Keeping the untouched arrays is what makes the refresh cheap: their identity survives, so the
 * page's own per-vehicle memos and the grid see them as unchanged.
 */
function rebuildRows(
  previous: Record<string, LiveDataRow[]>,
  readings: LatestReadings,
  dirty: Iterable<string>,
  lookups: LiveDataLookups,
): Record<string, LiveDataRow[]> {
  const rows = { ...previous };

  for (const deviceId of dirty) {
    const byDiagnostic = readings.get(deviceId);
    rows[deviceId] = [...(byDiagnostic?.values() ?? [])].map(entry =>
      toLiveDataRow(entry.item, lookups),
    );
  }

  return rows;
}
